import type {Router} from 'vue-router';
import {mainPages, consentRequiredRoute} from '@/ts/router/pages/main-pages';
import {publicPages} from '@/ts/router/pages/public-pages';
import {createI18nRouter} from '@/ts/router/router-utils';
import {contractExpiryNavigationGuard} from '@/ts/composables/stateful/use-contract-expiry-guard';
import {buildDocumentTitle} from '@/ts/composables/pure/use-document-title';
import {translate} from '@/ts/instances/i18n';

/** Creates the Vue Router instance for the main patient-facing app with i18n-prefixed routes */
export function routerFactory(): Router {
  // No hreflang alternates: they only mean something for URLs in the search index, and the app
  // asks not to be indexed (IPD-3003).
  const router = createI18nRouter({
    routes: [mainPages, consentRequiredRoute, ...publicPages],
  });

  // Lock expired-contract users out of non-exempt sections before the target
  // page mounts. Registered after createI18nRouter's guard so it runs once the
  // path is normalized and auth has been checked. Main app only — the admin app
  // (separate factory) has no contracts. See use-contract-expiry-guard.ts.
  router.beforeEach(contractExpiryNavigationGuard);

  // Tab title per route: a page that names itself carries a `titleKey`, the rest keep the product
  // name alone. Main app only — the admin dashboard keeps the static title from admin.html.
  // (`<html lang>` is set for both apps in createI18nRouter.)
  router.afterEach((to) => {
    const {titleKey} = to.meta;
    document.title = buildDocumentTitle(titleKey ? translate(titleKey) : null);
  });

  return router;
}
